import moment from 'moment';
import { TFunction } from 'react-i18next';
import { History } from 'history';
import { tail } from 'lodash';

import { analyticsClient } from 'src/shared/services/analytics';
import { CoachingVisitNode } from 'src/generated/webapp_gql';
import { ActionPropsWithId } from '@modernhealth/vital-components';
import getProviderInitials from 'src/modules/videoSessions/helpers/getProviderInitials';

const buildDefaultActionItemPayloadFromSession = ({
    history,
    session,
    t,
}: {
    history: History;
    session: CoachingVisitNode;
    t: TFunction;
}): ActionPropsWithId => {
    const title = t('dashboard:for_you.complete_payment_info.title');

    return {
        id: session.uuid,
        description: t('dashboard:for_you.complete_payment_info.subtitle', {
            date: moment(session.scheduledTime).format('D MMM YYYY'),
        }),
        title,
        onClick: () => {
            analyticsClient.trackClick(`For You Modal: ${title}`, {
                page_name: 'Homepage For You Modal',
            });

            history.push({
                pathname: '/payments/session-review',
                state: {
                    session: session.uuid,
                },
            });
        },
    };
};

const convertPendingPaymentSessionsToActionItems = ({
    history,
    sessions,
    t,
}: {
    history: History;
    sessions: CoachingVisitNode[];
    t: TFunction;
}) => {
    // We only want to show sessions in the Actions section that were booked by a provider.
    const paymentsSessionsBookedByProvider = sessions.filter(session => {
        const provider = session?.coachAssignment?.coach;

        return provider && session.createdByUuid === provider.uuid;
    });

    // Want to ignore the first element (next scheduled) in the sessions list since django-backend for you collection
    // builder will already display that next scheduled session in the Actions list. We only need to show the
    // subsequent sessions.
    // https://gitlab.com/modernhealth/django-backend/-/blob/686dc13f578890893d118d331c4ec5a687a0131b/modern_health/program/base/collection_builders/for_you_collection_builders.py#L233
    if (paymentsSessionsBookedByProvider.length <= 1) return [];

    return tail(paymentsSessionsBookedByProvider).reduce((accumulator, session) => {
        const provider = session?.coachAssignment?.coach;

        if (session && provider) {
            const actionItem: ActionPropsWithId = buildDefaultActionItemPayloadFromSession({
                history,
                session,
                t,
            });

            if (provider.avatar) {
                actionItem.startIconUrl = provider.avatar;
            } else {
                actionItem.initials = getProviderInitials(provider);
            }

            accumulator.push(actionItem);
        }

        return accumulator;
    }, [] as ActionPropsWithId[]);
};

export default convertPendingPaymentSessionsToActionItems;
